import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { FadeUp } from "@/components/Reveal";
import { ArrowButton } from "@/components/ArrowButton";
import { ARTICLES, findArticle } from "@/data/articles";
import { waLink, PROFESSIONAL } from "@/data/site";

const Artigo = () => {
  const { slug } = useParams();
  const article = findArticle(slug);
  if (!article) return <Navigate to="/conteudos" replace />;

  const others = ARTICLES.filter((a) => a.slug !== slug).slice(0, 2);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    author: { "@type": "Person", name: PROFESSIONAL.fullName, jobTitle: PROFESSIONAL.titles },
    publisher: { "@type": "Organization", name: PROFESSIONAL.brand },
    datePublished: article.date,
    inLanguage: "pt-BR",
  };

  return (
    <>
      <SEO title={`${article.title} | Sabrina Mota Psicóloga`} description={article.excerpt} path={`/conteudos/${article.slug}`} jsonLd={jsonLd} />
      <article className="pt-28 lg:pt-40" data-testid="artigo-page">
        <div className="container-x max-w-3xl pb-20 lg:pb-28">
          <FadeUp>
            <Link
              to="/conteudos"
              data-testid="artigo-voltar-link"
              className="group inline-flex items-center gap-2 text-sm text-raiz-700"
            >
              <ArrowLeft aria-hidden="true" className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
              Todos os conteúdos
            </Link>
          </FadeUp>
          <FadeUp delay={0.08}>
            <p className="eyebrow mt-10" data-testid="artigo-categoria">{article.category}</p>
            <h1 className="mt-4 font-serif text-3xl font-light leading-[1.15] tracking-tight text-ink sm:text-4xl lg:text-5xl" data-testid="artigo-title">
              {article.title}
            </h1>
            <p className="mt-5 text-sm text-ink/50">
              Por {PROFESSIONAL.name}, {PROFESSIONAL.titles} — {PROFESSIONAL.crp} · {article.minutes} min de leitura
            </p>
          </FadeUp>

          <div className="mt-12 space-y-10">
            {article.sections.map((s, i) => (
              <FadeUp key={i} delay={0.05}>
                <section data-testid={`artigo-secao-${i}`}>
                  {s.heading && (
                    <h2 className="font-serif text-2xl font-normal text-ink">{s.heading}</h2>
                  )}
                  <div className={`space-y-5 text-base leading-relaxed text-ink/75 ${s.heading ? "mt-4" : ""}`}>
                    {s.paragraphs.map((p, j) => (
                      <p key={j}>{p}</p>
                    ))}
                  </div>
                </section>
              </FadeUp>
            ))}
          </div>

          <FadeUp delay={0.1} className="mt-16 rounded-3xl bg-raiz-800 p-8 text-paper lg:p-10" data-testid="artigo-cta">
            <p className="font-serif text-2xl font-light">Ficou com alguma dúvida sobre o tema?</p>
            <p className="mt-3 text-sm leading-relaxed text-raiz-100/75">
              O primeiro contato pode ajudar a compreender melhor a sua demanda e o atendimento mais adequado.
            </p>
            <div className="mt-6">
              <ArrowButton href={waLink()} target="_blank" rel="noopener noreferrer" variant="light" testId="artigo-cta-button">
                Conversar no WhatsApp
              </ArrowButton>
            </div>
          </FadeUp>

          <div className="mt-16 border-t border-raiz-900/10 pt-10">
            <p className="text-xs uppercase tracking-[0.28em] text-raiz-600">Continue lendo</p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {others.map((o) => (
                <Link
                  key={o.slug}
                  to={`/conteudos/${o.slug}`}
                  data-testid={`artigo-relacionado-${o.slug}`}
                  className="group rounded-2xl border border-raiz-900/10 bg-cream p-6 transition-all duration-300 hover:-translate-y-1 hover:border-raiz-600/50"
                >
                  <p className="text-[11px] uppercase tracking-[0.24em] text-raiz-600">{o.category}</p>
                  <p className="mt-2 font-serif text-lg leading-snug text-ink">{o.title}</p>
                  <span className="mt-3 inline-flex items-center gap-2 text-sm text-raiz-700">
                    Ler
                    <ArrowRight aria-hidden="true" className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>
    </>
  );
};

export default Artigo;
